import React, { useEffect, useState } from 'react';
import ProfaseModal from '../../commons/ProfaseModal';
import PageForm, { FormField } from '../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../store';
import { getEntity, reset, updateEntity } from '../categoria-servicio.reducer';

const ACCENT_NORMAL = '#3b82f6';
const ACCENT_ESPECIAL = '#9a3412';

export interface ServicioEditFormValues {
  descripcion: string;
  alias: string;
  precio: string;
}

const fields: FormField<ServicioEditFormValues>[] = [
  { name: 'descripcion', label: 'Descripción', required: true, placeholder: 'Descripción del servicio' },
  { name: 'alias', label: 'Alias de Facturación', placeholder: 'Alias de facturación' },
  { name: 'precio', label: 'Precio', type: 'number', placeholder: '0.00' },
];

function makeEmptyForm(): ServicioEditFormValues {
  return { descripcion: '', alias: '', precio: '' };
}

function entityToForm(entity: Record<string, unknown>): ServicioEditFormValues {
  const precio = entity.precio;
  return {
    descripcion: String(entity.descripcion ?? ''),
    alias: String(entity.alias ?? entity.aliasFacturacion ?? entity.aliasDeFacturacion ?? ''),
    precio: precio != null && precio !== '' ? String(precio) : '',
  };
}

export interface ServicioEditarModalProps {
  open: boolean;
  servicioId: number | null;
  especial?: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export default function ServicioEditarModal({
  open,
  servicioId,
  especial = false,
  onClose,
  onSaved,
}: ServicioEditarModalProps) {
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.categoriaServicio.updating);

  const [loadedEntity, setLoadedEntity] = useState<Record<string, unknown>>({});
  const [form, setForm] = useState<ServicioEditFormValues>(() => makeEmptyForm());
  const [errors, setErrors] = useState<Partial<Record<keyof ServicioEditFormValues, string>>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open || servicioId == null) {
      setReady(false);
      setLoadError(null);
      setForm(makeEmptyForm());
      return;
    }

    let cancelled = false;
    setLoadError(null);
    setErrors({});
    setForm(makeEmptyForm());
    setReady(false);

    dispatch(getEntity(servicioId))
      .unwrap()
      .then((response: any) => {
        if (cancelled) return;
        const raw = (response?.data ?? response) as Record<string, unknown>;
        const data = raw && typeof raw === 'object' ? raw : {};
        setLoadedEntity(data);
        setForm(entityToForm(data));
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('No se pudo cargar el servicio.');
          setReady(false);
        }
      });

    return () => {
      cancelled = true;
      dispatch(reset());
    };
  }, [open, servicioId, dispatch]);

  const handleFieldChange = (name: keyof ServicioEditFormValues, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (servicioId == null || loadedEntity?.id == null) return;

    const errs: Partial<Record<keyof ServicioEditFormValues, string>> = {};
    if (!form.descripcion.trim()) errs.descripcion = 'La descripción es requerida';
    if (form.precio.trim() !== '' && Number.isNaN(Number(form.precio))) {
      errs.precio = 'Ingrese un precio válido';
    }
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const precioTrim = form.precio.trim();
    const payload = {
      ...loadedEntity,
      id: servicioId,
      descripcion: form.descripcion.trim(),
      alias: form.alias.trim(),
      precio: precioTrim === '' ? null : Number(precioTrim),
    };

    try {
      await dispatch(updateEntity(payload)).unwrap();
      onSaved?.();
      onClose();
    } catch {
      /* 403 u otro error: interceptor / UI global */
    }
  };

  if (!open || servicioId == null) return null;

  const showLoading = !ready && !loadError;

  return (
    <ProfaseModal
      open
      titleId="servicio-editar-modal-title"
      size="compact"
      zIndex={100010}
    >
      <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
        <h2 id="servicio-editar-modal-title" className="profase-modal-title m-0">
          {especial ? 'Editar servicio especial' : 'Editar servicio'}
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm flex-shrink-0"
          onClick={onClose}
          disabled={updating}
          aria-label="Cerrar"
        >
          Cerrar
        </button>
      </div>
      <p className="profase-modal-description">
        Modifique descripción, alias de facturación o precio.
      </p>
      {loadError && (
        <p className="profase-form-alert-error" style={{ margin: '0 0 16px' }} role="alert">
          {loadError}
        </p>
      )}
      {showLoading && (
        <p className="profase-form-muted-text" style={{ margin: '0 0 16px', fontSize: 14 }}>Cargando…</p>
      )}
      {ready && (
        <PageForm
          embedded
          fields={fields}
          values={form}
          errors={errors}
          isNew={false}
          saving={updating}
          labelInline
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
          onCancel={onClose}
          accentColor={especial ? ACCENT_ESPECIAL : ACCENT_NORMAL}
          submitLabel="Guardar cambios"
          cancelLabel="Cancelar"
        />
      )}
    </ProfaseModal>
  );
}
